import { computed, makeObservable, observable, reaction } from 'mobx'

import type AppStore from '@src/app/AppStore'
import VoicePhoneNumberSelectorController from '@src/app/voice/store/VoicePhoneNumberSelectorController'
import type {
  IdentityRow,
  PhoneNumberSelection,
} from '@src/component/phone-number-selector/PhoneNumberSelectorController'
import { getTypeLabel } from '@src/component/phone-number-selector/PhoneNumberSelectorController'
import isNonNull from '@src/lib/isNonNull'
import { logError } from '@src/lib/log'
import type { Identity, PhoneNumberModel } from '@src/service/model'
import { MemberModel, isDirectNumber } from '@src/service/model'

import DialerKeyboardListController from './DialerKeyboardListController'

export default class DialerController extends VoicePhoneNumberSelectorController {
  override readonly keyboardList = new DialerKeyboardListController(this)

  override lang = {
    SELECT_CUSTOM: 'Call ',
    PLACEHOLDER: 'Enter a name or phone number...',
  }
  override readonly size = 30

  constructor(app: AppStore) {
    super(app)
    makeObservable(this, {
      lang: observable,
      exclusions: computed,
    })

    this.disposeBag.add(
      reaction(
        () => this.app.service.flags.flags.groupCallingAddMultipleParticipants,
        (groupCallingAddMultipleParticipantsEnabled) => {
          if (groupCallingAddMultipleParticipantsEnabled) {
            this.lang.SELECT_CUSTOM = 'Add '
          } else {
            this.lang.SELECT_CUSTOM = 'Call '
          }
        },
        {
          fireImmediately: true,
          name: 'DialerController.GroupCallingAddMultipleParticipantsFlagChanged',
        },
      ),
      reaction(
        () => this.search,
        () => {
          if (this.search === 'greedisgood') {
            this.app.command.present({ name: 'change env' })
          }
        },
        { name: 'DialerController.SearchChanged' },
      ),
    )
  }

  get currentRow() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access -- FIXME: Fix this ESLint violation!
    return this.search ? this.keyboardList.stepper.item : null
  }

  override get currentPhoneNumber() {
    return null
  }

  override get exclusions(): Identity[] {
    return [this.currentUser as Identity].filter(isNonNull)
  }

  override get currentPhoneNumberMembers() {
    return []
  }

  override get hasExternalSelections(): boolean {
    return this.selections.some((s) => !isDirectNumber(this.convertSelectionToNumber(s)))
  }

  override get hasResults(): boolean {
    return !!this.search && super.hasResults
  }

  handleCall(fromNumber: PhoneNumberModel | null) {
    const from = fromNumber ?? this.app.service.user.directNumber

    if (from) {
      return this.app.voice
        .startCall(
          from,
          this.selections.map((selection) => ({
            number: this.convertSelectionToNumber(selection),
            userId: selection.to instanceof MemberModel ? selection.to.id : null,
            type: selection.type === 'add-number' ? 'number' : selection.type,
          })),
        )
        .catch((error) => {
          this.app.toast.showError(error)
          logError(error)
        })
    }
  }

  private convertSelectionToNumber(selection: PhoneNumberSelection): string {
    switch (selection.type) {
      case 'inbox':
        return selection.to.number
      case 'inbox-member':
        return selection.via
      case 'member':
        return selection.via
      case 'contact':
        return selection.via
      case 'add-number':
      case 'number':
        return selection.to
    }
  }

  override augmentIdentityRow(row: IdentityRow) {
    row.rowMeta.delete(row.id)

    const disabled = row.phones.length === 0
    if (!disabled) {
      return
    }

    const tooltipDisabledReason = `${getTypeLabel(row.type)} does not have a phone number`
    const labelDisabledReason = `No numbers`
    row.rowMeta.set(row.id, {
      disabled,
      tooltipDisabledReason,
      labelDisabledReason,
    })
  }
}
